import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Tabla } from "../../components/ui/primitivos";
import {
  actualizarPresupuesto,
  fetchCatalogo,
  fetchClientes,
  fetchPresupuesto,
  fetchProducto,
  fetchProductos,
  type RenglonPresupuesto,
  resolverTarifa,
} from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";
import { pesos } from "./consulta";
import {
  consultaDeTarifa,
  cuerpoDeRenglon,
  type EstadoDeTarifa,
  esSnapshot,
  iniciarConsultaDeTarifa,
  type RenglonArmado,
  subtotalRenglon,
  totalPresupuesto,
} from "./renglones";

/**
 * Edición de una consulta en BORRADOR — `/presupuestos/:id/editar` (ADR 0028).
 *
 * **Es una pantalla y no un modal** (ADR 0064), como `EditBudget.tsx` de
 * `imprenta`: el formulario lleva la cabecera, la forma de pago, el buscador de
 * producto con su precio en vivo y la tabla de renglones con su Total General.
 * Nada de eso entra cómodo en 512 px, que es lo que el modal daba.
 *
 * Los renglones se **reemplazan enteros**: lo que se manda es el estado final,
 * no una diferencia.
 */

/** Un renglón mientras se arma, con la clave que necesita React. */
type RenglonEnEdicion = RenglonArmado;

/**
 * El precio del renglón que se está cargando, tal como lo muestra `imprenta`
 * al lado de la cantidad.
 *
 * «Sin tarifa» y «error» son cosas distintas y se dicen distinto: la primera
 * es una respuesta del sistema —esa combinación no tiene precio vigente— y la
 * segunda es que no se pudo preguntar. Confundirlas manda a cargar un precio a
 * mano cuando lo que falló fue la red.
 */
function PrecioEnVivo({ estado, cantidad }: { estado: EstadoDeTarifa | null; cantidad: string }) {
  if (!estado) {
    return (
      <p className="text-neutral-500 text-sm">Elegí tamaño, base y cantidad para ver el precio</p>
    );
  }
  if (estado.estado === "buscando") {
    return <p className="text-neutral-500 text-sm">Buscando precio…</p>;
  }
  if (estado.estado === "sin-tarifa") {
    return (
      <p className="text-amber-700 text-sm">
        Sin tarifa vigente para esa combinación y cantidad — cargalo a mano
      </p>
    );
  }
  if (estado.estado === "error") {
    return <p className="text-red-700 text-sm">No se pudo consultar la tarifa: {estado.mensaje}</p>;
  }

  const subtotal = subtotalRenglon({
    cantidad: Number(cantidad) || 1,
    precioResuelto: estado.precio,
  });
  return (
    <div className="flex items-center justify-between rounded-md bg-neutral-50 px-3 py-2 text-sm">
      <span className="text-neutral-600">
        {pesos(Number(estado.precio.precio))}
        {estado.precio.esImporteFijo ? " (importe fijo)" : " c/u"}
        {estado.precio.usoPrecioVip ? " · VIP" : ""}
      </span>
      <span className="font-medium text-neutral-900">
        Subtotal {subtotal === null ? "—" : pesos(subtotal)}
      </span>
    </div>
  );
}

/**
 * El precio del renglón que se está cargando, resuelto contra la tarifa
 * mientras se elige — como el `usePriceLookup` de `imprenta`.
 *
 * La consulta se dispara sola al completar tamaño, base y cantidad, y se
 * cancela con cada cambio: lo que importa no es ahorrar requests sino que una
 * respuesta vieja no pise el precio de la cantidad actual.
 */
function usePrecioEnVivo(
  renglon: { tamanoId: string; baseId: string; cantidad: string },
  clienteId: string,
  activo: boolean,
): EstadoDeTarifa | null {
  const [estado, setEstado] = useState<EstadoDeTarifa | null>(null);
  const consulta = activo ? consultaDeTarifa(renglon, clienteId) : null;
  // La clave estabiliza el efecto: el objeto `consulta` es nuevo en cada render
  // y volvería a disparar la consulta con cada tecla ajena al renglón.
  const clave = consulta ? JSON.stringify(consulta) : "";

  useEffect(() => {
    if (!clave) {
      setEstado(null);
      return;
    }
    return iniciarConsultaDeTarifa(JSON.parse(clave), {
      buscar: (c, signal) => resolverTarifa(c, signal),
      alCambiar: setEstado,
    });
  }, [clave]);

  return estado;
}

/**
 * Alta y edición, el mismo formulario.
 *
 * Es un solo componente porque **el `PATCH` manda el estado final** y no una
 * diferencia (ADR 0028): editar es exactamente cargar la lista de renglones que
 * el presupuesto tiene que tener, que es lo que hace el alta. Dos formularios
 * separados divergirían en las validaciones.
 *
 * `imprenta` los tiene separados —`CreateBudget` y `EditBudget`, 128 y 308
 * líneas— porque su `BudgetForm` recibe el presupuesto entero; acá el estado del
 * formulario es la lista de renglones y arranca vacía o precargada.
 */
export function EditarConsultaPagina() {
  const { id = "" } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const volver = () => navegar(`/presupuestos/${id}`);

  const clientes = useDatos(() => fetchClientes());
  const productos = useDatos(() => fetchProductos());
  // Al editar, los renglones vienen del detalle: el listado no los trae.
  const original = useDatos(() => fetchPresupuesto(id), id);
  const [clienteId, setClienteId] = useState("");
  const [productoId, setProductoId] = useState("");
  const detalle = useDatos(
    () => (productoId ? fetchProducto(productoId) : Promise.resolve(null)),
    productoId,
  );
  const formasPago = useDatos(() => fetchCatalogo("formas-pago"));
  const [renglon, setRenglon] = useState({ tamanoId: "", baseId: "", cantidad: "1", manual: "" });
  // `imprenta` separa los dos modos con un toggle en vez de dejar el precio a
  // mano siempre a la vista: con los dos juntos, un importe tipeado por error
  // gana sobre la tarifa sin que nada lo diga.
  const [modoManual, setModoManual] = useState(false);
  const [nombreLibre, setNombreLibre] = useState("");
  // Los dos se precargan del DETALLE, no del listado: el listado no trae la
  // forma de pago.
  const [formaPagoId, setFormaPagoId] = useState("");
  const [validoHasta, setValidoHasta] = useState("");
  const [items, setItems] = useState<RenglonEnEdicion[]>([]);
  const [precargado, setPrecargado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const tarifa = usePrecioEnVivo(renglon, clienteId, !modoManual);

  // Se precarga UNA vez: si el efecto volviera a correr, pisaría los renglones
  // que la persona esté editando con los que tenía el servidor.
  useEffect(() => {
    if (precargado || !original.datos) return;
    setItems(
      original.datos.items.map(
        (i: RenglonPresupuesto): RenglonEnEdicion => ({
          clave: i.id,
          productoId: i.productoId,
          productoNombre: i.productoNombre ?? "Renglón manual",
          tamanoId: i.productoTamanoLogoId,
          baseId: i.productoTipoBaseId,
          cantidad: i.cantidad,
          // Un renglón sin snapshot se cotizó a mano: al reenviarlo hay que
          // mandar el importe, o el worker lo recotizaría contra la tarifa.
          ...(i.precioSnapshot === null && i.precioUnitario !== null
            ? { precioUnitario: Number(i.precioUnitario) }
            : {}),
          // El renglón ya cotizado muestra su importe sin volver a preguntar, y
          // lo saca del SNAPSHOT: el precio que vale es el que quedó congelado,
          // no el de hoy — y el snapshot ya trae el `total` con la regla de
          // `esImporteFijo` aplicada.
          ...(esSnapshot(i.precioSnapshot) ? { precioResuelto: i.precioSnapshot } : {}),
        }),
      ),
    );
    const cabecera = original.datos.presupuesto;
    setClienteId(cabecera.clienteId);
    if (cabecera.formaPagoId) setFormaPagoId(cabecera.formaPagoId);
    if (cabecera.validoHasta) setValidoHasta(cabecera.validoHasta.slice(0, 10));
    setPrecargado(true);
  }, [original.datos, precargado]);

  const producto = (productos.datos ?? []).find((p) => p.id === productoId);

  if (original.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Editar consulta" alVolver={volver} />
        <Aviso>{original.error}</Aviso>
      </div>
    );
  }

  const numero = original.datos?.presupuesto.numero;

  return (
    <div className="space-y-6">
      <PageHeader
        titulo={numero ? `Editar ${numero}` : "Editar consulta"}
        subtitulo="Los renglones se reemplazan enteros y el precio se vuelve a resolver"
        alVolver={volver}
      />

      <Card className="space-y-3 p-4">
        {error ? <Aviso>{error}</Aviso> : null}

        <div className="space-y-1">
          <label htmlFor="cliente" className="block text-neutral-600 text-sm">
            Cliente
          </label>
          <select
            id="cliente"
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">— elegir —</option>
            {(clientes.datos ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.razonSocial}
                {c.esVip ? " (VIP)" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Los dos campos de cabecera que `imprenta` tiene en `BudgetForm` y el
            worker ya aceptaba: hasta ahora la pantalla nunca los mandaba. */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label htmlFor="forma-pago" className="block text-neutral-600 text-sm">
              Forma de pago
            </label>
            <select
              id="forma-pago"
              value={formaPagoId}
              onChange={(e) => setFormaPagoId(e.target.value)}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">— sin definir —</option>
              {(formasPago.datos ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nombre}
                </option>
              ))}
            </select>
          </div>
          <Campo
            etiqueta="Válido hasta"
            type="date"
            value={validoHasta}
            onChange={(e) => setValidoHasta(e.target.value)}
          />
        </div>

        <div className="space-y-2 rounded-md border border-neutral-200 p-3">
          <div className="flex items-center justify-between">
            <p className="text-neutral-600 text-sm">Agregar renglón</p>
            {/* El toggle de `imprenta`: o se cotiza por catálogo, o se escribe
                el importe. Tener los dos a la vez deja que un número tipeado
                por error le gane a la tarifa sin que nada lo diga. */}
            <Boton
              variante="fantasma"
              onClick={() => {
                setModoManual((v) => !v);
                setRenglon({ tamanoId: "", baseId: "", cantidad: "1", manual: "" });
                setNombreLibre("");
                setProductoId("");
              }}
            >
              {modoManual ? "Usar catálogo" : "Cargar a mano"}
            </Boton>
          </div>

          {modoManual ? (
            <div className="space-y-2">
              <Campo
                etiqueta="Producto o servicio"
                value={nombreLibre}
                onChange={(e) => setNombreLibre(e.target.value)}
                ayuda="No sale del catálogo: el importe no queda respaldado por una tarifa"
              />
              <div className="grid grid-cols-2 gap-2">
                {/* `id` explícito: el primitivo lo deriva de la etiqueta, y los
                    dos modos tienen un campo «Cantidad». Con el mismo id, el
                    `<label>` de uno apunta al input del otro. */}
                <Campo
                  id="cantidad-libre"
                  etiqueta="Cantidad"
                  type="number"
                  min={1}
                  value={renglon.cantidad}
                  onChange={(e) => setRenglon((r) => ({ ...r, cantidad: e.target.value }))}
                />
                <Campo
                  etiqueta="Precio unitario"
                  type="number"
                  min={0}
                  step="0.01"
                  value={renglon.manual}
                  onChange={(e) => setRenglon((r) => ({ ...r, manual: e.target.value }))}
                />
              </div>
            </div>
          ) : null}
          {/* `hidden` además de la clase: la clase lo saca de la vista y el
              atributo lo saca del árbol de accesibilidad. Sin él, el lector de
              pantalla sigue anunciando los selects del modo que no está activo.
              Se esconde en vez de desmontarse para no perder la combinación ya
              elegida al ir y volver del modo manual. */}
          <div className={modoManual ? "hidden" : "space-y-2"} hidden={modoManual}>
            <select
              aria-label="Producto"
              value={productoId}
              onChange={(e) => {
                setProductoId(e.target.value);
                setRenglon((r) => ({ ...r, tamanoId: "", baseId: "" }));
              }}
              className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">— producto del catálogo —</option>
              {(productos.datos ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-2">
              <select
                aria-label="Tamaño"
                value={renglon.tamanoId}
                onChange={(e) => setRenglon((r) => ({ ...r, tamanoId: e.target.value }))}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
              >
                <option value="">— tamaño —</option>
                {(detalle.datos?.tamanos ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </select>
              <select
                aria-label="Base"
                value={renglon.baseId}
                onChange={(e) => setRenglon((r) => ({ ...r, baseId: e.target.value }))}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
              >
                <option value="">— base —</option>
                {(detalle.datos?.bases ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </select>
            </div>

            <Campo
              etiqueta="Cantidad"
              type="number"
              min={1}
              value={renglon.cantidad}
              onChange={(e) => setRenglon((r) => ({ ...r, cantidad: e.target.value }))}
            />

            {/* El precio, mientras se elige. Es la diferencia con `imprenta` que
              más se notaba: allá el importe aparece al completar la
              combinación, acá recién se sabía al guardar. */}
            <PrecioEnVivo estado={tarifa} cantidad={renglon.cantidad} />
          </div>

          <Boton
            variante="secundario"
            disabled={
              !renglon.cantidad ||
              (modoManual
                ? !nombreLibre.trim() || !renglon.manual
                : !renglon.tamanoId || !renglon.baseId || tarifa?.estado !== "encontrado")
            }
            onClick={() => {
              setItems((prev) => [
                ...prev,
                {
                  clave: crypto.randomUUID(),
                  productoId: modoManual ? null : productoId || null,
                  productoNombre: modoManual
                    ? nombreLibre.trim()
                    : (producto?.nombre ?? "Renglón manual"),
                  tamanoId: modoManual ? null : renglon.tamanoId || null,
                  baseId: modoManual ? null : renglon.baseId || null,
                  cantidad: Number(renglon.cantidad),
                  // El renglón se lleva el importe que se vio al agregarlo. Es
                  // el mismo que el worker va a congelar: sale de la misma
                  // resolución, con el mismo cliente y la misma cantidad.
                  ...(modoManual
                    ? { precioUnitario: Number(renglon.manual) }
                    : { precioResuelto: tarifa?.estado === "encontrado" ? tarifa.precio : null }),
                },
              ]);
              setRenglon({ tamanoId: "", baseId: "", cantidad: "1", manual: "" });
              setNombreLibre("");
            }}
          >
            Agregar
          </Boton>
        </div>

        {items.length > 0 ? (
          <>
            <Tabla cabeceras={["Renglón", "Cantidad", "Precio", "Subtotal", ""]}>
              {items.map((i) => {
                const subtotal = subtotalRenglon(i);
                return (
                  // Clave propia y no el índice: quitar un renglón del medio
                  // reindexa a los de abajo, y React reusaría la fila equivocada.
                  <tr key={String(i.clave)}>
                    <td className="px-4 py-1.5">
                      {String(i.productoNombre)}
                      {i.precioUnitario !== undefined ? (
                        <Etiqueta tono="aviso">a mano</Etiqueta>
                      ) : null}
                      {i.precioResuelto?.usoPrecioVip ? <Etiqueta tono="ok">VIP</Etiqueta> : null}
                    </td>
                    <td className="px-4 py-1.5 text-neutral-600">{String(i.cantidad)}</td>
                    <td className="px-4 py-1.5 text-neutral-600">
                      {i.precioUnitario !== undefined
                        ? pesos(i.precioUnitario)
                        : i.precioResuelto
                          ? `${pesos(Number(i.precioResuelto.precio))}${i.precioResuelto.esImporteFijo ? " fijo" : ""}`
                          : "sin tarifa"}
                    </td>
                    <td className="px-4 py-1.5">{subtotal === null ? "—" : pesos(subtotal)}</td>
                    <td className="px-4 py-1.5 text-right">
                      <Boton
                        variante="fantasma"
                        onClick={() => setItems((prev) => prev.filter((x) => x.clave !== i.clave))}
                      >
                        Quitar
                      </Boton>
                    </td>
                  </tr>
                );
              })}
            </Tabla>

            {/* El total, con la misma regla que el worker: un renglón sin
                precio lo deja indeterminado en vez de sumar de menos. */}
            <div className="flex items-center justify-between rounded-md bg-neutral-50 px-4 py-2 text-sm">
              <span className="text-neutral-600">Total</span>
              {(() => {
                const total = totalPresupuesto(items);
                return total === null ? (
                  <span className="text-amber-700">A definir — hay renglones sin precio</span>
                ) : (
                  <span className="font-medium text-neutral-900">{pesos(total)}</span>
                );
              })()}
            </div>
          </>
        ) : null}
      </Card>

      <div className="flex justify-end gap-2">
        <Boton variante="secundario" onClick={volver}>
          Cancelar
        </Boton>
        <Boton
          disabled={guardando || !clienteId || items.length === 0}
          onClick={async () => {
            setGuardando(true);
            setError(null);
            try {
              // `clave` y `productoNombre` existen solo para pintar la tabla de
              // acá. El endpoint los ignoraría, pero mandarlos sugeriría que
              // son parte del contrato.
              const cuerpo = {
                clienteId,
                ...(formaPagoId ? { formaPagoId } : {}),
                ...(validoHasta ? { validoHasta } : {}),
                items: items.map(cuerpoDeRenglon),
              };
              await actualizarPresupuesto(id, cuerpo);
              volver();
            } catch (err) {
              setError(err instanceof Error ? err.message : "No se pudo guardar");
            } finally {
              setGuardando(false);
            }
          }}
        >
          {guardando ? "Guardando..." : "Guardar"}
        </Boton>
      </div>
    </div>
  );
}
